import React, { useEffect, useMemo, useState } from 'react';
import { Image, Platform, Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import type { AccountSraDto, AccountUserSettingDto } from '../../../types/api';
import {
  SRA_ACCESS_COLUMNS,
  applySraEnableMapToConfigInfo,
  applySraToggleToEnableMap,
  getSraEnableWithDefault,
  sraSettingsToEnableMap,
} from '../../../services/account/SraMenuAccess';
import { Base64ImageStorage, extractImgSrc } from '../../../storage/Base64ImageStorage';
import { ConfigManager } from '../../../config/configManager';
import { EDITOR_COLORS, editorText, webControlStyle } from '../../../styles/editorTheme';

interface ToggleArrowIcons {
  upSrc: string | null;
  downSrc: string | null;
  loaded: boolean;
}

interface SiteOption {
  siteId: string;
  siteName: string;
}

interface MasterReassignOption {
  sraId: string;
  label: string;
}

interface SraRowEditorProps {
  row: AccountSraDto;
  expanded: boolean;
  lastLogin: string;
  accessTypeLabel?: string;
  onToggle: () => void;
  onConfigInfoChange?: (row: AccountSraDto, configInfo: AccountUserSettingDto[]) => void;
  onUpdateSra?: (nextRow: AccountSraDto, options?: { reassignMasterToSraId?: string }) => Promise<void>;
  siteOptions: SiteOption[];
  currentLoggedInSraId?: string;
  currentLoggedInSraEmail?: string;
  isMasterAccount?: boolean;
  canReassignMaster?: boolean;
  masterReassignCandidates?: MasterReassignOption[];
}

const UP_ARROW_IMAGE_ID = '101';
const DOWN_ARROW_IMAGE_ID = '100';

let cachedToggleArrowIcons: ToggleArrowIcons | null = null;
let toggleArrowIconsLoadPromise: Promise<ToggleArrowIcons> | null = null;

const EMPTY_TOGGLE_ARROWS: ToggleArrowIcons = {
  upSrc: null,
  downSrc: null,
  loaded: false,
};

const resolveRuntimeStorage = () => {
  return (globalThis as any).__proGroupsStorage
    ?? (globalThis as any).__childNavStorage
    ?? null;
};

const resolveImageSrc = (stored: string | null | undefined): string | null => {
  if (!stored) return null;

  const trimmed = stored.trim();
  if (!trimmed) return null;

  const extracted = extractImgSrc(trimmed);
  if (extracted) {
    return extracted;
  }

  if (trimmed.startsWith('data:image/')) {
    return trimmed;
  }

  if (/^[A-Za-z0-9+/=\s]+$/.test(trimmed) && trimmed.length > 80) {
    return `data:image/png;base64,${trimmed.replace(/\s+/g, '')}`;
  }

  return null;
};

const loadToggleArrowIcons = async (): Promise<ToggleArrowIcons> => {
  if (cachedToggleArrowIcons && (cachedToggleArrowIcons.downSrc || cachedToggleArrowIcons.upSrc)) {
    return cachedToggleArrowIcons;
  }

  if (toggleArrowIconsLoadPromise) {
    return toggleArrowIconsLoadPromise;
  }

  toggleArrowIconsLoadPromise = (async () => {
    try {
      const storage = resolveRuntimeStorage();
      if (!storage) {
        return { ...EMPTY_TOGGLE_ARROWS, loaded: false };
      }

      const imageStorage = new Base64ImageStorage(storage);
      const [downArrow, upArrow] = await Promise.all([
        imageStorage.getImage(DOWN_ARROW_IMAGE_ID),
        imageStorage.getImage(UP_ARROW_IMAGE_ID),
      ]);

      const nextIcons: ToggleArrowIcons = {
        downSrc: resolveImageSrc(downArrow?.base64),
        upSrc: resolveImageSrc(upArrow?.base64),
        loaded: true,
      };

      if (nextIcons.downSrc || nextIcons.upSrc) {
        cachedToggleArrowIcons = nextIcons;
      }

      return nextIcons;
    } catch (error) {
      console.warn('SraRowEditor: failed to load toggle arrows', error);
      return { ...EMPTY_TOGGLE_ARROWS, loaded: false };
    } finally {
      toggleArrowIconsLoadPromise = null;
    }
  })();

  return toggleArrowIconsLoadPromise;
};

const removeAccessTypeSuffix = (label: string): string => {
  return label.replace(/\s*\([^)]*\)\s*$/, '').trim();
};

const MASTER_REQUIRED_SETTINGS = ['enable_accountmenu', 'enable_manageteams'];

const isMasterRequiredSetting = (settingName: string): boolean => {
  const normalized = settingName.trim().toLowerCase();
  return MASTER_REQUIRED_SETTINGS.some((required) => required.toLowerCase() === normalized);
};

const ACCESS_TYPE_10_ALLOWED_SETTINGS = [
  'enable_viewprofiles',
  'enable_viewreports',
  'enable_usagestats',
  'enable_aggregate',
  'enable_managesubscription',
  'enable_accountsettings',
  'enable_assessmentoptions',
];

const ACCESS_TYPE_10_FORCE_DISABLED = [
  'enable_viewopenadmins',
  'enable_viewflags',
  'enable_viewmessaging',
  'enable_scheduledstats',
  'enable_systemstats',
  'enable_dataexport',
  'enable_managetouchpoints',
  'enable_manageteams',
];

const isAllowedForAccessType = (settingName: string, accessType: string | null | undefined): boolean => {
  const parsed = Number.parseInt((accessType || '').trim(), 10);
  if (parsed !== 10) {
    return true;
  }
  const normalized = settingName.trim().toLowerCase();
  return ACCESS_TYPE_10_ALLOWED_SETTINGS.some((allowed) => allowed.toLowerCase() === normalized);
};

const enforceAccessTypeRestrictions = (
  configInfo: AccountUserSettingDto[],
  accessType: string | null | undefined
): AccountUserSettingDto[] => {
  const parsed = Number.parseInt((accessType || '').trim(), 10);
  if (parsed !== 10) {
    return configInfo;
  }

  const normalized = new Set(ACCESS_TYPE_10_FORCE_DISABLED.map((name) => name.trim().toLowerCase()));
  return configInfo.map((setting) => {
    const settingName = (setting.Name || '').trim().toLowerCase();
    if (normalized.has(settingName)) {
      return { ...setting, Value: '0' };
    }
    return setting;
  });
};

export const SraRowEditor: React.FC<SraRowEditorProps> = ({
  row,
  expanded,
  lastLogin,
  accessTypeLabel,
  onToggle,
  onConfigInfoChange,
  onUpdateSra,
  siteOptions,
  currentLoggedInSraId,
  currentLoggedInSraEmail,
  isMasterAccount = false,
  canReassignMaster = false,
  masterReassignCandidates = [],
}) => {
  const [toggleArrowIcons, setToggleArrowIcons] = useState<ToggleArrowIcons>(cachedToggleArrowIcons ?? EMPTY_TOGGLE_ARROWS);
  const [nameValue, setNameValue] = useState(row.Name || '');
  const [emailValue, setEmailValue] = useState(row.Email || '');
  const [configEnableState, setConfigEnableState] = useState<Record<string, boolean>>(() => sraSettingsToEnableMap(row.ConfigInfo));
  const [selectedSiteId, setSelectedSiteId] = useState((row.SiteId || '').trim());
  const [reassignMasterToSraId, setReassignMasterToSraId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const rowIdentity = `${row.SraId || ''}:${row.SiteId || ''}:${row.Email || ''}`;
  const isWeb = Platform.OS === 'web';

  useEffect(() => {
    let cancelled = false;

    const refreshToggleArrows = async () => {
      const icons = await loadToggleArrowIcons();
      if (!cancelled) {
        setToggleArrowIcons(icons);
      }
    };

    void refreshToggleArrows();

    const removeListener = ConfigManager.addLogoImageListener(() => {
      void refreshToggleArrows();
    });

    return () => {
      cancelled = true;
      removeListener();
    };
  }, []);

  useEffect(() => {
    setNameValue(row.Name || '');
    setEmailValue(row.Email || '');
    setConfigEnableState(sraSettingsToEnableMap(row.ConfigInfo));
    setSelectedSiteId((row.SiteId || '').trim());
    setReassignMasterToSraId('');
    setUpdateError(null);
  }, [rowIdentity]);

  const resolvedSiteOptions = useMemo(() => {
    const rowSiteId = (row.SiteId || '').trim();
    if (!rowSiteId || siteOptions.some((entry) => entry.siteId === rowSiteId)) {
      return siteOptions;
    }

    return [{ siteId: rowSiteId, siteName: rowSiteId }, ...siteOptions];
  }, [row.SiteId, siteOptions]);

  const filteredColumns = useMemo(() => {
    return SRA_ACCESS_COLUMNS.map((column) => ({
      ...column,
      items: column.items.filter((item) => isAllowedForAccessType(item.settingName, row.AccessType)),
    }));
  }, [row.AccessType]);

  const maxEditorRows = Math.max(...filteredColumns.map((column) => column.items.length));

  const getEnabled = (settingName: string): boolean => getSraEnableWithDefault(configEnableState, settingName, true);

  const updateSetting = (settingName: string, nextValue: boolean) => {
    setConfigEnableState((current) => {
      const next = applySraToggleToEnableMap(current, settingName, nextValue);

      onConfigInfoChange?.(row, applySraEnableMapToConfigInfo(row.ConfigInfo, next));
      return next;
    });
  };

  const renderEnableCheckbox = (
    checked: boolean,
    onToggle: (nextChecked: boolean) => void,
    accessibilityLabel: string,
    disabled = false
  ) => {
    if (isWeb) {
      return React.createElement('input' as any, {
        type: 'checkbox',
        'aria-label': accessibilityLabel,
        checked,
        disabled,
        onChange: (event: any) => onToggle(Boolean(event?.target?.checked)),
        style: {
          width: '20px',
          height: '20px',
          accentColor: EDITOR_COLORS.accent,
          cursor: disabled ? 'not-allowed' : 'pointer',
        } as any,
      });
    }

    return (
      <Pressable
        onPress={() => onToggle(!checked)}
        style={styles.checkboxTouch}
        accessibilityRole="checkbox"
        accessibilityState={{ checked, disabled }}
        accessibilityLabel={accessibilityLabel}
        disabled={disabled}
      >
        <View style={[
          styles.checkboxBtn,
          checked && styles.checkboxBtnChecked,
          disabled && styles.checkboxBtnDisabled,
          disabled && checked && styles.checkboxBtnCheckedDisabled,
        ]}>
          {checked ? <Text style={styles.checkboxMark}>✓</Text> : null}
        </View>
      </Pressable>
    );
  };

  const editedConfigInfo = useMemo(
    () => applySraEnableMapToConfigInfo(row.ConfigInfo, configEnableState),
    [configEnableState, row.ConfigInfo]
  );

  const nameDirty = nameValue.trim() !== (row.Name || '').trim();
  const emailDirty = emailValue.trim() !== (row.Email || '').trim();
  const siteDirty = selectedSiteId !== (row.SiteId || '').trim();
  const configDirty = JSON.stringify(editedConfigInfo) !== JSON.stringify(row.ConfigInfo ?? []);
  const parsedStatus = Number.parseInt((row.Status || '').trim(), 10);
  const isDisabledStatus = Number.isFinite(parsedStatus)
    ? parsedStatus <= 0
    : (row.Status || '').trim() === '0';
  const isDeletedStatus = Number.isFinite(parsedStatus) && parsedStatus <= -2;
  const formReadOnly = isDisabledStatus || isSaving;
  const isCurrentLoggedInSra = useMemo(() => {
    const rowSraId = (row.SraId || '').trim();
    const rowEmail = (row.Email || '').trim().toLowerCase();
    const currentSraId = (currentLoggedInSraId || '').trim();
    const currentEmail = (currentLoggedInSraEmail || '').trim().toLowerCase();

    if (rowSraId && currentSraId && rowSraId === currentSraId) {
      return true;
    }

    if (rowEmail && currentEmail && rowEmail === currentEmail) {
      return true;
    }

    return false;
  }, [currentLoggedInSraEmail, currentLoggedInSraId, row.Email, row.SraId]);

  const reassignDirty = isMasterAccount && isCurrentLoggedInSra && canReassignMaster && !!reassignMasterToSraId.trim();
  const isDirty = nameDirty || emailDirty || siteDirty || configDirty || reassignDirty;

  const resolveSiteNameById = (siteId: string): string => {
    return resolvedSiteOptions.find((entry) => entry.siteId === siteId)?.siteName || siteId;
  };

  const cycleSite = () => {
    if (resolvedSiteOptions.length === 0) {
      return;
    }

    const currentIndex = resolvedSiteOptions.findIndex((entry) => entry.siteId === selectedSiteId);
    const nextIndex = currentIndex < 0 || currentIndex === resolvedSiteOptions.length - 1 ? 0 : currentIndex + 1;
    setSelectedSiteId(resolvedSiteOptions[nextIndex].siteId);
  };

  const cycleReassignMasterTarget = () => {
    if (masterReassignCandidates.length === 0) {
      return;
    }

    const ordered = [''].concat(masterReassignCandidates.map((entry) => entry.sraId));
    const currentIndex = ordered.indexOf(reassignMasterToSraId);
    const nextIndex = currentIndex < 0 || currentIndex === ordered.length - 1 ? 0 : currentIndex + 1;
    setReassignMasterToSraId(ordered[nextIndex]);
  };

  const webSelectStyle = { ...webControlStyle, width: '220px', height: 28 } as any;

  const onUpdate = async () => {
    if (!onUpdateSra || !isDirty || formReadOnly) {
      return;
    }

    const finalConfigInfo = enforceAccessTypeRestrictions(editedConfigInfo, row.AccessType);

    const nextRow: AccountSraDto = {
      ...row,
      Name: nameValue.trim(),
      Email: emailValue.trim(),
      SiteId: selectedSiteId,
      ConfigInfo: finalConfigInfo,
    };

    try {
      setIsSaving(true);
      setUpdateError(null);
      await onUpdateSra(nextRow, {
        reassignMasterToSraId: isMasterAccount && isCurrentLoggedInSra && reassignMasterToSraId.trim()
          ? reassignMasterToSraId.trim()
          : undefined,
      });
      setReassignMasterToSraId('');
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setUpdateError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const onToggleAccessStatus = async () => { 
    if (!onUpdateSra || isSaving || isMasterAccount) {
      return;
    }

    const nextStatus = isDisabledStatus ? '1' : '0';
    const finalConfigInfo = enforceAccessTypeRestrictions(editedConfigInfo, row.AccessType);
    const nextRow: AccountSraDto = {
      ...row,
      Name: nameValue.trim(),
      Email: emailValue.trim(),
      SiteId: selectedSiteId,
      ConfigInfo: finalConfigInfo,
      Status: nextStatus,
    };

    try {
      setIsSaving(true);
      setUpdateError(null);
      await onUpdateSra(nextRow);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setUpdateError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const onDeleteAccess = async () => {
    if (!onUpdateSra || isSaving || isDeletedStatus || isMasterAccount) {
      return;
    }

    const finalConfigInfo = enforceAccessTypeRestrictions(editedConfigInfo, row.AccessType);
    const nextRow: AccountSraDto = {
      ...row,
      Name: nameValue.trim(),
      Email: emailValue.trim(),
      SiteId: selectedSiteId,
      ConfigInfo: finalConfigInfo,
      Status: '-2',
    };

    try {
      setIsSaving(true);
      setUpdateError(null);
      await onUpdateSra(nextRow);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setUpdateError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const activeArrowSrc = expanded ? toggleArrowIcons.upSrc : toggleArrowIcons.downSrc;
  const resolvedAccessTypeLabelBase = accessTypeLabel?.trim() || row.AccessType || '-';
  const resolvedAccessTypeLabel = isMasterAccount && !resolvedAccessTypeLabelBase.includes('*')
    ? `${resolvedAccessTypeLabelBase} *`
    : resolvedAccessTypeLabelBase;

  return (
    <>
      <View style={[styles.row, expanded && styles.rowExpanded]}>
        <View style={styles.toggleCol}>
          <Pressable onPress={onToggle} style={styles.expandButton} accessibilityRole="button" accessibilityLabel="Toggle SRA row details">
            {activeArrowSrc ? (
              <Image source={{ uri: activeArrowSrc }} style={styles.expandImage} resizeMode="contain" />
            ) : (
              <Text style={styles.expandButtonText}>{expanded ? '▲' : '▼'}</Text>
            )}
          </Pressable>
        </View>

        <View style={styles.nameCol}>
          {expanded ? (
            <TextInput
              style={[styles.inlineInput, formReadOnly && styles.inlineInputDisabled]}
              value={nameValue}
              onChangeText={setNameValue}
              editable={!formReadOnly}
              autoCapitalize="words"
              placeholder="Name"
              placeholderTextColor={EDITOR_COLORS.placeholder}
            />
          ) : (
            <Text style={[styles.primaryText, isDisabledStatus && styles.primaryTextDisabled]}>{row.Name || '-'}</Text>
          )}
        </View>

        <View style={styles.emailCol}>
          {expanded ? (
            <TextInput
              style={[styles.inlineInput, formReadOnly && styles.inlineInputDisabled]}
              value={emailValue}
              onChangeText={setEmailValue}
              editable={!formReadOnly}
              autoCapitalize="none"
              keyboardType="email-address"
              placeholder="Email"
              placeholderTextColor={EDITOR_COLORS.placeholder}
            />
          ) : (
            <Text style={[styles.primaryText, isDisabledStatus && styles.primaryTextDisabled]}>{row.Email || '-'}</Text>
          )}
        </View>

        <View style={styles.lastLoginCol}>
          <Text style={styles.primaryText}>{lastLogin}</Text>
        </View>

        <View style={styles.accessTypeCol}>
          <Text style={styles.primaryText}>{resolvedAccessTypeLabel}</Text>
        </View>
      </View>

      {expanded ? (
        <View style={styles.editPanel}>
          <View style={styles.editPanelLeft}>

            <View style={styles.accessTableWrap}>
              <View style={styles.accessHeaderRow}>
                {filteredColumns.map((column, columnIndex) => {
                  const columnEnabled = getEnabled(column.menuSettingName);
                  return (
                    <React.Fragment key={column.section}>
                      <View style={styles.accessHeaderCell}>
                        {renderEnableCheckbox(
                          columnEnabled,
                          (nextChecked) => updateSetting(column.menuSettingName, nextChecked),
                          `Toggle ${column.section} menu`,
                          formReadOnly || (isMasterAccount && isMasterRequiredSetting(column.menuSettingName))
                        )}
                        <Text style={styles.accessHeaderLabel}>{column.section}</Text>
                      </View>
                      {columnIndex < filteredColumns.length - 1 ? <View style={styles.accessSpacerCol} /> : null}
                    </React.Fragment>
                  );
                })}
              </View>

              {Array.from({ length: maxEditorRows }).map((_, rowIndex) => (
                <View key={`access-row-${rowIndex}`} style={styles.accessDataRow}>
                  {filteredColumns.map((column, columnIndex) => {
                    const item = column.items[rowIndex];
                    const columnEnabled = getEnabled(column.menuSettingName);

                    if (!item) {
                      return (
                        <React.Fragment key={`${column.section}-empty-${rowIndex}`}>
                          <View style={styles.accessDataCell} />
                          {columnIndex < SRA_ACCESS_COLUMNS.length - 1 ? <View style={styles.accessSpacerCol} /> : null}
                        </React.Fragment>
                      );
                    }

                    const rowEnabled = getEnabled(item.settingName);
                    const disabled = !columnEnabled || formReadOnly || (isMasterAccount && isMasterRequiredSetting(item.settingName));
                    return (
                      <React.Fragment key={`${column.section}-${item.settingName}`}>
                        <View style={styles.accessDataCell}>
                          {renderEnableCheckbox(
                            rowEnabled,
                            (nextChecked) => updateSetting(item.settingName, nextChecked),
                            `Toggle ${item.label}`,
                            disabled
                          )}
                          <Text style={[styles.accessItemLabel, disabled && styles.accessItemLabelDisabled]}>{item.label}</Text>
                        </View>
                        {columnIndex < filteredColumns.length - 1 ? <View style={styles.accessSpacerCol} /> : null}
                      </React.Fragment>
                    );
                  })}
                </View>
              ))}
            </View>

            <View style={styles.siteEditorRow}>
              <Text style={styles.siteEditorLabel}>Site</Text>
              <View style={styles.siteEditorInputWrap}>
                {isWeb ? (
                  React.createElement(
                    'select' as any,
                    {
                      value: selectedSiteId,
                      onChange: (event: any) => setSelectedSiteId(String(event?.target?.value ?? '')),
                      disabled: formReadOnly,
                      style: webSelectStyle,
                      'aria-label': 'SRA site',
                    },
                    resolvedSiteOptions.map((option) => (
                      React.createElement('option' as any, { key: option.siteId, value: option.siteId }, option.siteName)
                    ))
                  )
                ) : (
                  <Pressable onPress={cycleSite} style={styles.sitePickerBtn} accessibilityRole="button" accessibilityLabel="SRA site" disabled={formReadOnly}>
                    <Text style={styles.sitePickerBtnText} numberOfLines={1}>{resolveSiteNameById(selectedSiteId)}</Text>
                    <Text style={styles.sitePickerChevron}>▼</Text>
                  </Pressable>
                )}
              </View>
            </View>

            {isMasterAccount && isCurrentLoggedInSra && canReassignMaster && masterReassignCandidates.length > 0 ? (
              <View style={[styles.siteEditorRow, styles.reassignEditorRow]}>
                <Text style={[styles.siteEditorLabel, styles.reassignEditorLabel]}>Reassign Master Account</Text>
                <View style={[styles.siteEditorInputWrap, styles.reassignEditorInputWrap]}>
                  {isWeb ? (
                    React.createElement(
                      'select' as any,
                      {
                        value: reassignMasterToSraId,
                        onChange: (event: any) => setReassignMasterToSraId(String(event?.target?.value ?? '')),
                        disabled: formReadOnly,
                        style: webSelectStyle,
                        'aria-label': 'Reassign master account',
                      },
                      React.createElement('option' as any, { key: '__none__', value: '' }, 'Keep current master'),
                      masterReassignCandidates.map((option) => (
                        React.createElement('option' as any, { key: option.sraId, value: option.sraId }, removeAccessTypeSuffix(option.label))
                      ))
                    )
                  ) : (
                    <Pressable
                      onPress={cycleReassignMasterTarget}
                      style={styles.sitePickerBtn}
                      accessibilityRole="button"
                      accessibilityLabel="Reassign master account"
                      disabled={formReadOnly}
                    >
                      <Text style={styles.sitePickerBtnText} numberOfLines={1}>
                        {reassignMasterToSraId
                          ? removeAccessTypeSuffix(masterReassignCandidates.find((entry) => entry.sraId === reassignMasterToSraId)?.label || reassignMasterToSraId)
                          : 'Keep current master'}
                      </Text>
                      <Text style={styles.sitePickerChevron}>▼</Text>
                    </Pressable>
                  )}
                </View>
              </View>
            ) : null}
          </View>

          <View style={styles.editPanelRight}>
            {isDisabledStatus ? (
              !isCurrentLoggedInSra ? (
                <Pressable
                  style={[styles.menuLinkBtn, styles.menuLinkBtnDelete, (isSaving || isDeletedStatus || isMasterAccount) && styles.menuLinkBtnDisabled]}
                  accessibilityRole="button"
                  accessibilityLabel="Delete Access"
                  onPress={onDeleteAccess}
                  disabled={isSaving || isDeletedStatus || isMasterAccount}
                >
                  <Text style={[styles.menuLinkText, styles.menuLinkTextDanger]}>
                    {isDeletedStatus ? 'Access Deleted' : (isSaving ? 'Deleting...' : 'Delete Access')}
                  </Text>
                </Pressable>
              ) : null
            ) : (
              <Pressable style={styles.menuLinkBtn} accessibilityRole="button" accessibilityLabel="Send Invite">
                <Text style={styles.menuLinkText}>Send Invite</Text>
              </Pressable>
            )}
            {!isCurrentLoggedInSra && !isMasterAccount ? (
              <Pressable
                style={[styles.menuLinkBtn, styles.menuLinkBtnStatus, isSaving && styles.menuLinkBtnDisabled]}
                accessibilityRole="button"
                accessibilityLabel={isDisabledStatus ? 'Re-enable Access' : 'Disable Access'}
                onPress={onToggleAccessStatus}
                disabled={isSaving}
              >
                <Text style={styles.menuLinkText}>{isDisabledStatus ? 'Re-enable Access' : 'Disable Access'}</Text>
              </Pressable>
            ) : null}
            {!isDisabledStatus ? (
              <Pressable
                style={[styles.menuLinkBtn, styles.menuLinkBtnUpdate, (!isDirty || isSaving) && styles.menuLinkBtnDisabled]}
                accessibilityRole="button"
                accessibilityLabel="Update SRA"
                onPress={onUpdate}
                disabled={!isDirty || formReadOnly}
              >
                <Text style={styles.menuLinkText}>{isSaving ? 'Updating...' : 'Update'}</Text>
              </Pressable>
            ) : null}
            {updateError ? <Text style={styles.updateErrorText}>{updateError}</Text> : null}
          </View>
        </View>
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  row: {
    minHeight: 30,
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
    backgroundColor: EDITOR_COLORS.background,
  },
  rowExpanded: {
    borderBottomWidth: 0,
  },
  toggleCol: {
    width: '8%',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingLeft: 8,
  },
  nameCol: {
    width: '24%',
    paddingHorizontal: 8,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  emailCol: {
    width: '32%',
    paddingHorizontal: 8,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  lastLoginCol: {
    width: '18%',
    paddingHorizontal: 8,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  accessTypeCol: {
    width: '18%',
    paddingHorizontal: 8,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  expandButton: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  expandButtonText: {
    fontSize: 15,
    lineHeight: 15,
    color: EDITOR_COLORS.textSecondary,
    fontFamily: 'Arial',
  },
  expandImage: {
    width: 9,
    height: 9,
  },
  primaryText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    textAlign: 'left',
  },
  primaryTextDisabled: {
    color: EDITOR_COLORS.textMuted,
    fontStyle: 'italic',
  },
  inlineInput: {
    ...editorText,
    width: '100%',
    height: 26,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    color: EDITOR_COLORS.textPrimary,
    backgroundColor: EDITOR_COLORS.background,
  },
  inlineInputDisabled: {
    color: EDITOR_COLORS.textMuted,
    borderColor: '#d1d5db',
    backgroundColor: '#f8fafc',
  },
  editPanel: {
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
    backgroundColor: EDITOR_COLORS.background,
    flexDirection: 'row',
    alignItems: 'stretch',
    paddingHorizontal: 10,
    paddingTop: 0,
    paddingBottom: 8,
    gap: 12,
  },
  editPanelLeft: {
    flex: 1,
    backgroundColor: EDITOR_COLORS.background,
    padding: 10,
  },
  editPanelRight: {
    width: 160,
    backgroundColor: EDITOR_COLORS.background,
    borderLeftWidth: 1,
    borderLeftColor: EDITOR_COLORS.separator,
    paddingTop: 22,
    paddingRight: 0,
    paddingBottom: 10,
    paddingLeft: 12,
    gap: 6,
  },
  editPanelSubheading: {
    ...editorText,
    marginTop: 3,
    color: EDITOR_COLORS.textMuted,
    marginBottom: 10,
  },
  accessTableWrap: {
    backgroundColor: EDITOR_COLORS.background,
  },
  siteEditorRow: {
    marginTop: 10,
    flexDirection: 'row',
    alignItems: 'center',
  },
  siteEditorLabel: {
    ...editorText,
    width: 50,
    paddingLeft: 15,
    color: EDITOR_COLORS.textPrimary,
  },
  siteEditorInputWrap: {
    width: 220,
    maxWidth: '100%',
  },
  reassignEditorRow: {
    alignItems: 'center',
  },
  reassignEditorLabel: {
    width: 170,
    lineHeight: 18,
  },
  reassignEditorInputWrap: {
    flex: 1,
    width: 'auto',
    minWidth: 220,
  },
  sitePickerBtn: {
    width: '100%',
    height: 28,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    backgroundColor: EDITOR_COLORS.background,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sitePickerBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    flex: 1,
    marginRight: 6,
  },
  sitePickerChevron: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  accessHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: EDITOR_COLORS.background,
  },
  accessHeaderCell: {
    flex: 1,
    minHeight: 34,
    paddingHorizontal: 8,
    paddingVertical: 4,
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
  },
  accessSpacerCol: {
    width: 18,
    backgroundColor: EDITOR_COLORS.background,
  },
  accessHeaderLabel: {
    ...editorText,
    marginLeft: 8,
    color: EDITOR_COLORS.textPrimary,
  },
  accessDataRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    backgroundColor: EDITOR_COLORS.background,
  },
  accessDataCell: {
    flex: 1,
    minHeight: 30,
    paddingHorizontal: 8,
    paddingVertical: 4,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: EDITOR_COLORS.background,
  },
  accessItemLabel: {
    ...editorText,
    marginLeft: 8,
    color: EDITOR_COLORS.textSecondary,
  },
  accessItemLabelDisabled: {
    color: EDITOR_COLORS.placeholder,
  },
  checkboxTouch: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxBtn: {
    width: 20,
    height: 20,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.accent,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: EDITOR_COLORS.background,
  },
  checkboxBtnChecked: {
    backgroundColor: EDITOR_COLORS.accent,
    borderColor: EDITOR_COLORS.accent,
  },
  checkboxBtnDisabled: {
    borderColor: EDITOR_COLORS.controlBorder,
    backgroundColor: '#f8fafc',
  },
  checkboxBtnCheckedDisabled: {
    borderColor: EDITOR_COLORS.placeholder,
    backgroundColor: EDITOR_COLORS.placeholder,
  },
  checkboxMark: {
    fontFamily: 'Arial',
    fontSize: 15,
    color: EDITOR_COLORS.background,
    lineHeight: 15,
    fontWeight: '800',
  },
  menuLinkBtn: {
    minHeight: 26,
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  menuLinkBtnUpdate: {
    marginTop: 14,
  },
  menuLinkBtnStatus: {
    marginTop: 8,
  },
  menuLinkBtnDelete: {
    marginTop: 8,
  },
  menuLinkBtnDisabled: {
    opacity: 0.45,
  },
  menuLinkText: {
    ...editorText,
    color: EDITOR_COLORS.accent,
    textDecorationLine: 'none',
  },
  menuLinkTextDanger: {
    color: EDITOR_COLORS.errorText,
  },
  updateErrorText: {
    ...editorText,
    marginTop: 4,
    color: EDITOR_COLORS.errorText,
    lineHeight: 17,
  },
});
